import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Role,
  RolePermission,
  UserPermissionOverride
} from '@authority-labs/entities';
import { environment } from '@check-list/environments/environment';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

const API = `${environment.API}/permissions`;

@Injectable({
  providedIn: 'root'
})
export class AppPermissionsService {
  private permissions = new Set<string>();
  private permissions$ = new BehaviorSubject<Set<string>>(this.permissions);

  constructor(private http: HttpClient) {}

  // ─── Permission Check ─────────────────────────────────────────────

  has(key: string): boolean {
    return this.permissions.has(key);
  }

  hasAny(...keys: string[]): boolean {
    return keys.some((k) => this.permissions.has(k));
  }

  hasAll(...keys: string[]): boolean {
    return keys.every((k) => this.permissions.has(k));
  }

  get changes$(): Observable<Set<string>> {
    return this.permissions$.asObservable();
  }

  // ─── Load / Clear ─────────────────────────────────────────────────

  /**
   * Load resolved permissions for the current user from the API.
   * Called after login.
   */
  loadPermissions(): Observable<{ permissions: string[] }> {
    return this.http.get<{ permissions: string[] }>(`${API}/me`).pipe(
      tap((resp) => {
        this.permissions = new Set(resp.permissions);
        this.permissions$.next(this.permissions);
      }),
      catchError(() => of({ permissions: [] as string[] }))
    );
  }

  /** Set permissions from a pre-loaded array (e.g., from cached data) */
  setPermissions(keys: string[]): void {
    this.permissions = new Set(keys);
    this.permissions$.next(this.permissions);
  }

  clear(): void {
    this.permissions.clear();
    this.permissions$.next(this.permissions);
  }

  // ─── Admin API: Roles ─────────────────────────────────────────────

  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${API}/roles`);
  }

  getRole(id: number): Observable<Role> {
    return this.http.get<Role>(`${API}/roles/${id}`);
  }

  createRole(data: {
    key: string;
    name: string;
    description?: string;
  }): Observable<Role> {
    return this.http.post<Role>(`${API}/roles`, data);
  }

  updateRole(
    id: number,
    data: { name?: string; description?: string; isDefault?: boolean }
  ): Observable<Role> {
    return this.http.put<Role>(`${API}/roles/${id}`, data);
  }

  deleteRole(id: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${API}/roles/${id}`);
  }

  // ─── Admin API: Role Permissions ──────────────────────────────────

  getRolePermissions(roleId: number): Observable<RolePermission[]> {
    return this.http.get<RolePermission[]>(
      `${API}/roles/${roleId}/permissions`
    );
  }

  setRolePermissions(
    roleId: number,
    permissions: { permissionKey: string; granted: boolean }[]
  ): Observable<RolePermission[]> {
    return this.http.put<RolePermission[]>(
      `${API}/roles/${roleId}/permissions`,
      { permissions }
    );
  }

  // ─── Admin API: User Overrides ────────────────────────────────────

  getUserOverrides(userId: number): Observable<UserPermissionOverride[]> {
    return this.http.get<UserPermissionOverride[]>(
      `${API}/users/${userId}/overrides`
    );
  }

  setUserOverrides(
    userId: number,
    overrides: { permissionKey: string; granted: boolean }[]
  ): Observable<UserPermissionOverride[]> {
    return this.http.put<UserPermissionOverride[]>(
      `${API}/users/${userId}/overrides`,
      { overrides }
    );
  }

  // ─── Admin API: Permission Keys Registry ──────────────────────────

  getPermissionKeys(): Observable<{
    keys: string[];
    groups: {
      module: string;
      label: string;
      keys: { key: string; label: string }[];
    }[];
  }> {
    return this.http.get<any>(`${API}/keys`);
  }
}
